import React, { useState, useEffect, useRef } from 'react'
import type { RefereeListItem } from '../types'
import { useReferees, RefereeScopeKey, RefereeSortKey } from '../swr-config'
import { useLeague, isLiveTodayMode, LEAGUES, COUNTRY_FLAGS, type League } from '../context/LeagueContext'
import { useNavigation } from '../context/NavigationContext'
import { useFeatureTracking, FeatureNames } from '../hooks/useActivityTracking'
import { IoRefresh, IoChevronUp, IoChevronDown } from 'react-icons/io5'

interface RefereesPageProps {
  onRefereeSelect: (refereeId: number) => void
  /** ID of referee to highlight and scroll to (from search navigation) */
  highlightRefereeId?: number | null
}

const SCOPE_LABELS: Record<RefereeScopeKey, string> = {
  season: 'Season',
  last10: 'Last 10',
  last5: 'Last 5',
}

export function RefereesPage({ onRefereeSelect, highlightRefereeId }: RefereesPageProps) {
  // Track feature usage
  useFeatureTracking(FeatureNames.REFEREES)

  const { currentLeague } = useLeague()
  const { scrollToRefereeId, clearScrollToReferee } = useNavigation()
  const [scope, setScope] = useState<RefereeScopeKey>('season')
  const [sortBy, setSortBy] = useState<RefereeSortKey>('matches')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  // Local league selection - always synced with global league (filtered to regular leagues only)
  const isLiveToday = isLiveTodayMode(currentLeague)

  // Get the initial league: use currentLeague if it's a regular league, otherwise default to EPL
  const getInitialLeague = (): League => {
    if (!isLiveToday && currentLeague.type === 'league') {
      return currentLeague
    }
    return LEAGUES[0] // Default to EPL
  }

  const [selectedLeague, setSelectedLeague] = useState<League>(getInitialLeague)

  // Sync selectedLeague when global league changes (from sidebar)
  useEffect(() => {
    if (!isLiveToday && currentLeague.type === 'league') {
      setSelectedLeague(currentLeague)
    }
  }, [currentLeague, isLiveToday])

  // The selected league is always what we use for referee data
  const effectiveLeague = selectedLeague

  // Refs for scrolling to highlighted referee
  const rowRefs = useRef<Map<number, HTMLTableRowElement>>(new Map())

  const { data, error, isLoading, mutate } = useReferees({
    leagueId: effectiveLeague.api_league_id,
    scope,
    sortBy,
    sortOrder,
  })

  // Scroll to highlighted referee when data loads or highlightRefereeId changes
  useEffect(() => {
    const targetId = highlightRefereeId ?? scrollToRefereeId
    if (!targetId || !data?.referees) return

    // Small delay to ensure refs are set
    const timer = setTimeout(() => {
      const row = rowRefs.current.get(targetId)
      if (row) {
        row.scrollIntoView({ behavior: 'smooth', block: 'start' })
        // Clear the scroll-to state after scrolling
        if (scrollToRefereeId === targetId) {
          clearScrollToReferee()
        }
      }
    }, 100)

    return () => clearTimeout(timer)
  }, [highlightRefereeId, scrollToRefereeId, data?.referees, clearScrollToReferee])

  const handleSort = (key: RefereeSortKey) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')
    } else {
      setSortBy(key)
      setSortOrder('desc')
    }
  }

  const SortIcon = ({ column }: { column: RefereeSortKey }) => {
    if (sortBy !== column) return null
    return sortOrder === 'desc' ? <IoChevronDown size={14} /> : <IoChevronUp size={14} />
  }

  const getStrictnessBadgeClass = (label: string) => {
    switch (label) {
      case 'strict':
        return 'strictness-strict'
      case 'lenient':
        return 'strictness-lenient'
      default:
        return 'strictness-average'
    }
  }

  return (
    <div className="referees-page">
      <div className="card">
        <div className="card-inner">
          <div className="page-header">
            <div className="page-header-main">
              <h1>Referees</h1>
              <button
                className="refresh-btn"
                onClick={() => mutate()}
                disabled={isLoading}
                title="Refresh"
              >
                <IoRefresh size={18} className={isLoading ? 'spinning' : ''} />
              </button>
            </div>
            <p className="page-subtitle">
              {effectiveLeague.name} referee statistics and card tendencies
            </p>
          </div>

          {/* League selector - always shown */}
          <div className="page-league-selector">
            <span className="page-league-selector-label">Select League:</span>
            <div className="page-league-selector-chips">
              {LEAGUES.filter(l => l.type === 'league').map((league) => (
                <button
                  key={league.api_league_id}
                  className={`chip ${selectedLeague.api_league_id === league.api_league_id ? 'chip-active' : ''}`}
                  onClick={() => setSelectedLeague(league)}
                >
                  <span className="chip-flag">{COUNTRY_FLAGS[league.country]}</span>
                  {league.shortName}
                </button>
              ))}
            </div>
          </div>

          {/* Scope toggle */}
          <div className="row" style={{ gap: 8, marginBottom: 16 }}>
            {(['season', 'last10', 'last5'] as RefereeScopeKey[]).map((s) => (
              <button
                key={s}
                className={`chip ${scope === s ? 'chip-active' : ''}`}
                onClick={() => setScope(s)}
              >
                {SCOPE_LABELS[s]}
              </button>
            ))}
          </div>

          {/* Table */}
          <div className="referees-table-wrapper">
            <table className="referees-table">
              <thead>
                <tr>
                  <th>Referee</th>
                  <th className="sortable" onClick={() => handleSort('matches')}>
                    Matches <SortIcon column="matches" />
                  </th>
                  <th className="sortable" onClick={() => handleSort('yellows_per_90')}>
                    Yellows/G <SortIcon column="yellows_per_90" />
                  </th>
                  <th className="sortable" onClick={() => handleSort('reds_per_90')}>
                    Reds/G <SortIcon column="reds_per_90" />
                  </th>
                  <th className="sortable" onClick={() => handleSort('fouls_per_90')}>
                    Fouls/G <SortIcon column="fouls_per_90" />
                  </th>
                  <th className="sortable" onClick={() => handleSort('fouls_per_card')}>
                    Fouls/Card <SortIcon column="fouls_per_card" />
                  </th>
                  <th className="sortable" onClick={() => handleSort('strictness')}>
                    Strictness <SortIcon column="strictness" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading && !data && (
                  <>
                    {Array.from({ length: 10 }).map((_, i) => (
                      <tr key={i} className="skeleton-row">
                        <td colSpan={7}>
                          <div className="skeleton-line" />
                        </td>
                      </tr>
                    ))}
                  </>
                )}
                {data?.referees.map((ref) => {
                  const isHighlighted = ref.id === highlightRefereeId || ref.id === scrollToRefereeId
                  return (
                    <tr
                      key={ref.id}
                      ref={(el) => {
                        if (el) rowRefs.current.set(ref.id, el)
                        else rowRefs.current.delete(ref.id)
                      }}
                      className={`referee-row clickable ${isHighlighted ? 'highlighted' : ''}`}
                      onClick={() => onRefereeSelect(ref.id)}
                    >
                      <td className="referee-name">
                        <span>{ref.name}</span>
                        {ref.country && <span className="country">{ref.country}</span>}
                      </td>
                      <td>{ref.matches}</td>
                      <td>{ref.yellows_per_90.toFixed(2)}</td>
                      <td>{ref.reds_per_90.toFixed(2)}</td>
                      <td>{ref.fouls_per_90.toFixed(1)}</td>
                      <td>{ref.fouls_per_card.toFixed(1)}</td>
                      <td>
                        <span className={`strictness-badge ${getStrictnessBadgeClass(ref.strictness_label)}`}>
                          {ref.strictness_label}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {error && (
            <div className="error-state">
              <p>Error loading referees</p>
              <button className="chip" onClick={() => mutate()}>
                Retry
              </button>
            </div>
          )}

          {data && data.referees.length === 0 && (
            <div className="empty-state">
              <p>No referee data available for this league.</p>
            </div>
          )}

          {data && data.total_count > 0 && (
            <div className="results-summary">
              {data.total_count} referees &bull; League avg: {data.league_averages.yellows_per_90.toFixed(2)}{' '}
              yellows/game
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
